import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
//
// Copyright IBM Corp. 2022
//
// This source code is licensed under the Apache-2.0 license found in the
// LICENSE file in the root directory of this source tree.
//
import React from 'react';
import { Button, Checkbox, RadioButton, StructuredListRow, StructuredListWrapper, StructuredListBody, Dropdown } from 'carbon-components-react';
import { ChevronRight16, View16 } from '@carbon/icons-react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { pkg } from '../../settings';
import { UserProfileImage } from '../UserProfileImage';
var blockClass = "".concat(pkg.prefix, "--add-select__selections");
var componentName = 'AddSelectList';
export var AddSelectList = function AddSelectList(_ref) {
  var _modifiers$options;

  var appliedModifiers = _ref.appliedModifiers,
      displayMetalPanel = _ref.displayMetalPanel,
      filteredItems = _ref.filteredItems,
      metaIconDescription = _ref.metaIconDescription,
      modifiers = _ref.modifiers,
      multi = _ref.multi,
      multiSelection = _ref.multiSelection,
      navIconDescription = _ref.navIconDescription,
      parentId = _ref.parentId,
      setAppliedModifiers = _ref.setAppliedModifiers,
      setDisplayMetaPanel = _ref.setDisplayMetaPanel,
      setMultiSelection = _ref.setMultiSelection,
      setParentSelected = _ref.setParentSelected,
      setSingleSelection = _ref.setSingleSelection,
      singleSelection = _ref.singleSelection;
  var hasModifiers = (modifiers === null || modifiers === void 0 ? void 0 : (_modifiers$options = modifiers.options) === null || _modifiers$options === void 0 ? void 0 : _modifiers$options.length) > 0;

  var handleSingleSelection = function handleSingleSelection(value) {
    setSingleSelection(value);
  };

  var handleMultiSelection = function handleMultiSelection(value, id) {
    if (value) {
      var newValues = [].concat(_toConsumableArray(multiSelection), [id]);
      setMultiSelection(newValues);
    } else {
      var _newValues = multiSelection.filter(function (v) {
        return v !== id;
      });

      setMultiSelection(_newValues);
    }
  };

  var onNavigateItem = function onNavigateItem(_ref2) {
    var id = _ref2.id,
        title = _ref2.title;
    setParentSelected(id, title, parentId);
  };

  var isSelected = function isSelected(id) {
    if (multi) {
      return multiSelection.includes(id);
    }

    return id === singleSelection;
  };

  var getAvatarProps = function getAvatarProps(_ref3) {
    var src = _ref3.src,
        alt = _ref3.alt,
        icon = _ref3.icon,
        backgroundColor = _ref3.backgroundColor;
    return {
      className: "".concat(blockClass, "-cell-avatar"),
      size: 'lg',
      theme: 'light',
      image: src,
      imageDescription: alt,
      icon: icon,
      backgroundColor: backgroundColor
    };
  };

  var getItemIcon = function getItemIcon(_ref4) {
    var Icon = _ref4.icon;
    return /*#__PURE__*/React.createElement(Icon, null);
  };

  var modifierHandler = function modifierHandler(id, selectedItem) {
    var modifiersClone = _toConsumableArray(appliedModifiers);

    var modifierIdx = modifiersClone.findIndex(function (item) {
      return item.id === id;
    });
    modifiersClone[modifierIdx] = _defineProperty({
      id: id
    }, modifiers.id, selectedItem);
    setAppliedModifiers(modifiersClone);
  };

  var metaPanelHandler = function metaPanelHandler(item) {
    if (item.meta) {
      setDisplayMetaPanel(item);
    }
  };

  var isInMetaPanel = function isInMetaPanel(id) {
    return id === (displayMetalPanel === null || displayMetalPanel === void 0 ? void 0 : displayMetalPanel.id);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: cx("".concat(blockClass, "-wrapper"), _defineProperty({}, "".concat(blockClass, "-wrapper-multi"), multi))
  }, /*#__PURE__*/React.createElement(StructuredListWrapper, {
    selection: true,
    className: "".concat(blockClass)
  }, /*#__PURE__*/React.createElement(StructuredListBody, null, filteredItems.map(function (item) {
    var _cx2;

    return /*#__PURE__*/React.createElement(StructuredListRow, {
      key: item.id,
      className: cx("".concat(blockClass, "-row"), (_cx2 = {}, _defineProperty(_cx2, "".concat(blockClass, "-row-selected"), isSelected(item.id)), _defineProperty(_cx2, "".concat(blockClass, "-row-meta-selected"), isInMetaPanel(item.id)), _cx2)),
      onClick: function onClick(evt) {
        return metaPanelHandler(item, evt);
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "".concat(blockClass, "-cell")
    }, /*#__PURE__*/React.createElement("div", {
      className: "".concat(blockClass, "-cell-wrapper")
    }, multi ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "".concat(blockClass, "-checkbox")
    },
    /*#__PURE__*/
    // hacky way to prevent checkbox from triggering the meta onclick handler
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    React.createElement("div", {
      onClick: function onClick(event) {
        return event.stopPropagation();
      }
    }, /*#__PURE__*/React.createElement(Checkbox, {
      onChange: function onChange(value, id) {
        return handleMultiSelection(value, id);
      },
      labelText: item.title,
      id: item.id,
      checked: isSelected(item.id),
      className: "".concat(blockClass, "-checkbox-wrapper")
    })), /*#__PURE__*/React.createElement("div", {
      className: "".concat(blockClass, "-checkbox-label-wrapper")
    }, item.avatar && /*#__PURE__*/React.createElement(UserProfileImage, getAvatarProps(item.avatar)), item.icon && /*#__PURE__*/React.createElement("div", {
      className: "".concat(blockClass, "-cell-icon")
    }, getItemIcon(item)), /*#__PURE__*/React.createElement("div", {
      className: "".concat(blockClass, "-checkbox-label-text")
    }, /*#__PURE__*/React.createElement("span", {
      className: "".concat(blockClass, "-cell-title")
    }, item.title), item.subtitle && /*#__PURE__*/React.createElement("span", {
      className: "".concat(blockClass, "-cell-subtitle")
    }, item.subtitle)))), hasModifiers && /*#__PURE__*/React.createElement(Dropdown, {
      id: "add-select-modifier-".concat(item.id),
      type: "inline",
      items: modifiers.options,
      light: true,
      label: modifiers.label,
      disabled: !isSelected(item.id),
      className: "".concat(blockClass, "-dropdown ").concat(blockClass, "-hidden-hover"),
      initialSelectedItem: item[modifiers.id],
      onChange: function onChange(_ref5) {
        var selectedItem = _ref5.selectedItem;
        return modifierHandler(item.id, selectedItem);
      }
    })) : /*#__PURE__*/React.createElement(RadioButton, {
      className: "".concat(blockClass, "-radio"),
      name: "add-select-selections",
      id: item.id,
      value: item.value,
      labelText: item.title,
      onChange: function onChange() {
        return handleSingleSelection(item.id);
      },
      checked: isSelected(item.id)
    }), item.children && /*#__PURE__*/React.createElement(Button, {
      className: "".concat(blockClass, "-view-children"),
      renderIcon: ChevronRight16,
      iconDescription: navIconDescription,
      tooltipPosition: "left",
      tooltipAlignment: "center",
      hasIconOnly: true,
      onClick: function onClick() {
        return onNavigateItem(item);
      },
      kind: "ghost",
      size: "sm"
    }), item.meta && /*#__PURE__*/React.createElement("div", {
      className: "".concat(blockClass, "-hidden-hover")
    }, /*#__PURE__*/React.createElement(Button, {
      className: "".concat(blockClass, "-view-meta"),
      renderIcon: View16,
      iconDescription: metaIconDescription,
      tooltipPosition: "left",
      tooltipAlignment: "center",
      hasIconOnly: true,
      kind: "ghost",
      size: "sm"
    })))));
  }))));
};
AddSelectList.propTypes = {
  appliedModifiers: PropTypes.array,
  displayMetalPanel: PropTypes.object,
  filteredItems: PropTypes.array,
  metaIconDescription: PropTypes.string,
  modifiers: PropTypes.object,
  multi: PropTypes.bool,
  multiSelection: PropTypes.array,
  navIconDescription: PropTypes.string,
  parentId: PropTypes.string,
  setAppliedModifiers: PropTypes.func,
  setDisplayMetaPanel: PropTypes.func,
  setMultiSelection: PropTypes.func,
  setParentSelected: PropTypes.func,
  setSingleSelection: PropTypes.func,
  singleSelection: PropTypes.string
};
AddSelectList.displayName = componentName;